import { useVideoPlayer } from '@ad-lucy/context';
import { type PlayerControlType, useGlobalConfig } from '@ad-lucy/context/adData';
import { usePages } from '@ad-lucy/context/pages';
import { useEventListener } from '@ad-lucy/hooks';
import { EVENT, EventsUtils } from '@ad-lucy/utils';
import { useCallback, useEffect, useState } from 'react';

import { LiveRenderingEvent, type LiveRenderingReceiveMessage } from '../../../types';
import { useReceiveMessage } from '../../useReceiveMessage';

function useLiveRenderingVideoPlay() {
  const { videoPlayer } = useVideoPlayer();
  const { data: globalConfig } = useGlobalConfig();
  const [playClicked, setPlayClicked] = useState(false);
  const { currentPageIndex, pages } = usePages();

  const playerControlType = globalConfig?.meta.video_playerControlType as PlayerControlType;

  const progressListener = useCallback(() => {
    if (playClicked || !videoPlayer) return;
    const currentTime = Math.round(videoPlayer.currentTime());
    const nextPageIndex = ((currentPageIndex ?? 0) + 1) % pages.length;
    let nextPageShown = pages[nextPageIndex]?.meta.showon as number;

    if (nextPageIndex === 0) nextPageShown = videoPlayer.duration();
    if (currentTime < nextPageShown) return;

    EventsUtils.AppEventEmitter.Instance.emit(EVENT.PAUSE);
  }, [videoPlayer, currentPageIndex, pages, playClicked]);

  const onReceiveMessage = useCallback(
    (data: LiveRenderingReceiveMessage) => {
      const { event, payload } = data;
      if (event === LiveRenderingEvent.CHANGE_PAGE && videoPlayer) {
        const pageIndex = pages.findIndex((page) => page.id === payload);

        if (pageIndex !== -1) {
          const timeStamp = pages[pageIndex]?.meta.showon as number;
          videoPlayer.currentTime(timeStamp);
          if (playerControlType !== 'Countdown') setPlayClicked(false);
          EventsUtils.AppEventEmitter.Instance.emit(EVENT.PLAY);
        }
      }
    },
    [videoPlayer, pages, playerControlType],
  );

  useReceiveMessage({ callback: onReceiveMessage });

  const onPlay = useCallback(() => {
    setPlayClicked(true);
  }, []);

  const onTimelineClick = useCallback((): void => {
    setPlayClicked(false);
    EventsUtils.AppEventEmitter.Instance.emit(EVENT.PLAY);
  }, []);

  const onWindowClick = useCallback((event: MouseEvent) => {
    if ((event.target as HTMLElement).closest('.CountdownBP__PlayButton')) {
      setPlayClicked(true);
    }
  }, []);

  useEffect(() => {
    if (!videoPlayer) return;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const controlBar = videoPlayer.controlBar as any;
    controlBar?.playToggle?.on('click', onPlay);
    videoPlayer.on('timeupdate', progressListener);
    controlBar?.progressControl?.on('click', onTimelineClick);
    controlBar?.progressControl?.seekBar?.on('click', onTimelineClick);

    return () => {
      controlBar?.playToggle?.off('click', onPlay);
      videoPlayer.off('timeupdate', progressListener);
      controlBar?.progressControl?.off('click', onTimelineClick);
      controlBar?.progressControl?.seekBar?.off('click', onTimelineClick);
    };
  }, [videoPlayer, progressListener, onPlay, onTimelineClick]);

  useEventListener('click', onWindowClick);
}

export default useLiveRenderingVideoPlay;
